import { FC } from "react";
import type { DelegatedKeyState } from "@/hooks/useDelegatedKeyStatus";

interface DelegatedKeyCardProps {
  state: DelegatedKeyState;
  /** False while the signing context is still coming up. */
  canSign: boolean;
  onSign: () => void;
  /** Stop waiting on the wallet and offer the button again. */
  onDismiss: () => void;
}

/**
 * What the account's private data is waiting on, and the one action that
 * resolves it.
 *
 * This exists because the same underlying condition used to be invisible: the
 * app auto-prompted the wallet, and if that popup opened behind the window --
 * or the prompt was skipped because a background precondition was not ready --
 * the page simply showed no balances, with nothing to click and nothing to
 * read. The "check for a popup" line is the specific fix for the former, and
 * the button is the fix for the latter.
 */
export const DelegatedKeyCard: FC<DelegatedKeyCardProps> = ({
  state,
  canSign,
  onSign,
  onDismiss,
}) => {
  if (state.status === "ready") return null;

  // A deposit has to be absorbed before the account exists, and no signature
  // can hurry that along -- so this state offers no button, only the reason.
  if (state.status === "waiting-for-account") {
    return (
      <div className="w-full rounded-xl border border-sr-pink-muted/30 bg-sr-pink-muted/10 p-4">
        <p className="text-sm font-medium text-sr-pink-light">
          Waiting for your deposit to settle
        </p>
        <p className="mt-2 text-xs text-sr-neutral-300">
          Your account is created once the deposit is absorbed, usually within a
          few minutes. Your balances appear here automatically — you don't need
          to do anything.
        </p>
      </div>
    );
  }

  const signing = state.status === "signing";
  const registering = state.status === "registering";

  return (
    <div className="w-full rounded-xl border border-sr-tangerine/40 bg-sr-tangerine/10 p-4">
      <p className="text-base font-medium text-sr-tangerine">
        {registering ? "Connecting your account" : "Connect your account"}
      </p>

      <div className="mt-2 flex flex-col gap-2 text-sm text-sr-neutral-300">
        {signing ? (
          <p className="font-medium text-sr-tangerine">
            Check for a popup window from your wallet — it may have opened
            behind this one.
          </p>
        ) : registering ? (
          <p>
            Signed. Waiting for the ledger to register your key; this usually
            takes a few seconds.
          </p>
        ) : (
          <p>
            Sign once with your wallet to let this browser read your balances
            and orders. This authorises reading only — it cannot move funds.
          </p>
        )}

        {state.status === "failed" && state.error && (
          <p className="text-sr-tangerine">{state.error}</p>
        )}
      </div>

      {/* The primary action is hidden while registering: there is nothing to
          retry until the ledger answers, and re-prompting the wallet mid-flight
          is how the old flow produced duplicate signature requests. */}
      {!registering && (
        <button
          type="button"
          className="sr-btn-primary mt-3 w-full disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!canSign || signing}
          onClick={onSign}
        >
          {signing
            ? "Waiting for your wallet…"
            : state.status === "failed"
              ? "Try again"
              : "Sign to connect"}
        </button>
      )}

      {/* An unanswered popup -- closed, or hidden behind the window -- produces
          no rejection to observe, so without this the card would wait forever
          on a disabled button. Registering gets the same escape: the ledger
          normally answers in seconds, and a wait much longer than that means
          something went wrong rather than slowly. */}
      {(signing || registering) && (
        <button
          type="button"
          className="mt-2 w-full text-xs text-sr-neutral-300 underline underline-offset-2 hover:text-white"
          onClick={onDismiss}
        >
          {signing ? "Didn't see a popup? Start over" : "Taking too long? Start over"}
        </button>
      )}
    </div>
  );
};
